/**
 * Material Form Component
 *
 * Form for creating and editing inventory items
 *
 * @module pages/inventory/MaterialForm
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  InputGroup,
} from 'react-bootstrap';
import { toast } from '../../utils/toast';
import {
  createInventoryItem,
  updateInventoryItem,
  getInventoryItem,
  uploadInventoryImage,
  removeInventoryImage,
} from '../../services/inventoryService';
import { useAuth } from '../../contexts/AuthContext';
import { UNITS, INVENTORY_CATEGORY_LABELS } from '../../utils/constants';
import DashboardLayout from '../../components/layout/DashboardLayout';
// Reutilizado tal cual del módulo de herramientas: `tool_locations` es el
// catálogo compartido de lugares físicos, no se crea un picker de bodegas
// paralelo.
import LocationPicker from '../../components/locations/LocationPicker';

/**
 * Normaliza un input numérico vacío a `null`.
 *
 * En yup, `.nullable()` NO cubre el string vacío que manda un `<input
 * type="number">` sin tocar: el cast a número convierte `''` en `NaN`, que no es
 * `null`, así que `.positive()` se evalúa igual y falla. El resultado era que
 * `sale_price` y `unit_cost` se comportaban como obligatorios y > 0 pese a estar
 * declarados como opcionales — y los 54 materiales activos de producción los
 * tienen en NULL, así que abrir cualquiera de ellos y guardar era imposible:
 * el form se quejaba de un precio que nadie había puesto nunca.
 *
 * El `.transform()` corre ANTES de las validaciones y devuelve `null` para el
 * blanco, con lo que `.nullable()` recién ahí hace lo que dice. El `NaN` que
 * viene de un texto no numérico se deja pasar tal cual para que `.typeError()`
 * siga avisando.
 */
const emptyStringToNull = (value, originalValue) =>
  originalValue === '' || originalValue === null || originalValue === undefined
    ? null
    : value;

// Form validation schema
export const materialSchema = yup.object().shape({
  name: yup
    .string()
    .required('El nombre es requerido')
    .min(3, 'El nombre debe tener al menos 3 caracteres')
    .max(200, 'El nombre no puede exceder 200 caracteres'),
  sku: yup
    .string()
    .max(50, 'El SKU no puede exceder 50 caracteres'),
  description: yup
    .string()
    .max(500, 'La descripcion no puede exceder 500 caracteres'),
  category: yup
    .string()
    .nullable(),
  unit: yup
    .string()
    .required('La unidad es requerida'),
  sale_price: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .positive('El precio debe ser mayor a 0')
    .typeError('Debe ser un numero valido'),
  unit_cost: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .positive('El costo debe ser mayor a 0')
    .typeError('Debe ser un numero valido'),
  current_stock: yup
    .number()
    .required('El stock actual es requerido')
    .min(0, 'El stock no puede ser negativo')
    .typeError('Debe ser un numero valido'),
  min_stock_level: yup
    .number()
    .required('El stock minimo es requerido')
    .min(0, 'El stock minimo no puede ser negativo')
    .typeError('Debe ser un numero valido'),
  track_batches: yup
    .boolean()
    .default(true),
  // #103: gate between "stock + FIFO" and "direct project expense" flows.
  is_stockable: yup
    .boolean()
    .default(true),
  // Decisión 8 (Jorge): bodega donde vive el material. Opcional en el form —
  // dejarlo vacío NO significa "sin bodega": la DB lo cae a la bodega
  // principal (DEFAULT de columna + trigger de coerción, migración
  // 20260727220000). Por eso no lleva `.required()`: la regla vive en el
  // servidor, donde ningún carril la puede saltar, no en la validación de un
  // formulario.
  default_location_id: yup
    .string()
    .nullable(),
});

const MaterialForm = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: materialId } = useParams();
  const [searchParams] = useSearchParams();
  const { isAdmin, isTechnician, isSupervisor, loading: authLoading } = useAuth();
  const isEditMode = !!materialId;
  // #798 — el supervisor da de ALTA materiales y nada mas (policy
  // `supervisor_insert_inventory`: solo INSERT). No edita, y no tiene pantalla
  // de inventario: `/inventario` sigue admin-only, asi que al terminar no se lo
  // puede mandar ahi (el RoleGuard lo rebotaria). Tampoco sube la foto: el
  // bucket `inventory-images` no le acepta el INSERT y guardar la ruta es un
  // UPDATE, que tampoco tiene. Las dos cosas estan pendientes de decision.
  const soloAlta = isSupervisor && !isAdmin;
  const puedeUsarElFormulario = isAdmin || isTechnician || (isSupervisor && !isEditMode);
  const puedeSubirImagen = isAdmin || isTechnician;
  const rutaDeSalida = soloAlta ? '/herramientas' : '/inventario';
  const initialItemType = !isEditMode && searchParams.get('tipo') === 'tool' ? 'tool' : 'consumable';

  const [loading, setLoading] = useState(false);
  const [fetchingMaterial, setFetchingMaterial] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [autoGenerateSKU, setAutoGenerateSKU] = useState(!isEditMode);

  // Image state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [removingImage, setRemovingImage] = useState(false);

  // React Hook Form
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(materialSchema),
    defaultValues: {
      name: '',
      sku: '',
      description: '',
      category: '',
      item_type: initialItemType,
      unit: 'unidad',
      sale_price: '',
      unit_cost: '',
      current_stock: 0,
      min_stock_level: 0,
      track_batches: true,
      is_stockable: true,
      default_location_id: '',
    },
  });

  const watchName = watch('name');
  const watchItemType = watch('item_type');

  // Generate SKU from name
  useEffect(() => {
    if (autoGenerateSKU && watchName) {
      const prefix = watchName
        .substring(0, 3)
        .toUpperCase()
        .replace(/[^A-Z]/g, '');
      const random = Math.floor(1000 + Math.random() * 9000);
      setValue('sku', `${prefix}${random}`);
    }
  }, [watchName, autoGenerateSKU, setValue]);

  // Check permissions
  useEffect(() => {
    if (authLoading) return;
    if (!puedeUsarElFormulario) {
      toast.error('No tienes permisos para gestionar el inventario');
      navigate(rutaDeSalida);
    }
  }, [authLoading, puedeUsarElFormulario, rutaDeSalida, navigate]);

  // Fetch material data if editing
  useEffect(() => {
    if (!isEditMode) return;

    const fetchMaterialData = async () => {
      try {
        setFetchingMaterial(true);
        const { data, error } = await getInventoryItem(materialId);

        if (error) throw error;

        reset({
          name: data.name || '',
          sku: data.sku || '',
          description: data.description || '',
          category: data.category || '',
          item_type: data.item_type || 'consumable',
          unit: data.unit || 'unidad',
          sale_price: data.sale_price || '',
          unit_cost: data.unit_cost || '',
          current_stock: data.current_stock || 0,
          min_stock_level: data.min_stock_level || 0,
          track_batches: data.track_batches !== false,
          is_stockable: data.is_stockable !== false,
          default_location_id: data.default_location_id || '',
        });
        setAutoGenerateSKU(false);

        // Load existing image
        if (data.image_url) {
          setExistingImageUrl(data.image_url);
          setImagePreview(data.image_url);
        }
      } catch (error) {
        console.error('Fetch material error:', error);
        setError(error.message);
        toast.error('Error al cargar el material');
      } finally {
        setFetchingMaterial(false);
      }
    };

    fetchMaterialData();
  }, [materialId, isEditMode, reset]);

  // Handle image file selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de archivo no permitido. Use JPG, PNG o WebP.');
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('El archivo excede el tamaño máximo de 5MB.');
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  // Handle image removal
  const handleRemoveImage = async () => {
    if (isEditMode && existingImageUrl) {
      // Remove from server
      setRemovingImage(true);
      try {
        const result = await removeInventoryImage(materialId, existingImageUrl);
        if (result.error) throw result.error;
        toast.success('Imagen eliminada');
      } catch (err) {
        toast.error('Error al eliminar la imagen');
        setRemovingImage(false);
        return;
      }
      setRemovingImage(false);
    }

    // Clear local state
    setImageFile(null);
    setImagePreview(null);
    setExistingImageUrl(null);
  };

  // Handle form submission
  const onSubmit = async (formData) => {
    try {
      setLoading(true);
      setError(null);

      const cleanData = {
        ...formData,
        sku: formData.sku || null,
        description: formData.description || null,
      };

      // `default_location_id` es uuid: un '' del <select> reventaría el INSERT
      // ("invalid input syntax for type uuid"). Se OMITE la clave en vez de
      // mandar null, para que el DEFAULT de columna (bodega principal) aplique
      // en el alta. En edición, omitirla deja la bodega que ya tenía.
      if (!cleanData.default_location_id) {
        delete cleanData.default_location_id;
      }

      let result;
      if (isEditMode) {
        result = await updateInventoryItem(materialId, cleanData);
      } else {
        result = await createInventoryItem(cleanData);
      }

      if (result.error) throw result.error;

      const itemId = isEditMode ? materialId : result.data.id;

      // Upload image if a new file was selected
      if (imageFile && puedeSubirImagen) {
        setUploadingImage(true);
        const uploadResult = await uploadInventoryImage(imageFile, itemId);
        if (uploadResult.error) {
          toast.warning('Material guardado, pero hubo un error al subir la imagen');
          console.error('Image upload error:', uploadResult.error);
        } else {
          // #568 — se guarda la RUTA del objeto (`path`), no la URL firmada
          // (`url`): la URL vence en 1 hora y la foto se muere sola. La URL se
          // firma fresca en cada lectura (`inventoryService`).
          await updateInventoryItem(itemId, { image_url: uploadResult.path });
        }
        setUploadingImage(false);
      }

      const label = formData.item_type === 'tool' ? 'Herramienta' : 'Material';
      toast.success(isEditMode ? `${label} actualizado exitosamente` : `${label} creado exitosamente`);

      queryClient.invalidateQueries(['inventory']);
      queryClient.invalidateQueries(['inventory-summary']);
      queryClient.invalidateQueries(['inventory-valuation']);

      navigate(rutaDeSalida);
    } catch (error) {
      console.error('Submit error:', error);
      setError(error.message);
      toast.error(isEditMode ? 'Error al actualizar' : 'Error al crear el ítem');
    } finally {
      setLoading(false);
      setUploadingImage(false);
    }
  };

  const handleCancel = () => {
    navigate(rutaDeSalida);
  };

  if (fetchingMaterial) {
    return (
      <DashboardLayout>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando material...</span>
          </Spinner>
        </Container>
      </DashboardLayout>
    );
  }

  if (error && isEditMode) {
    return (
      <DashboardLayout>
        <Container className="mt-4">
          <Alert variant="danger">
            <Alert.Heading>Error al cargar el material</Alert.Heading>
            <p>{error}</p>
            <Button variant="outline-danger" onClick={() => navigate('/inventario')}>
              Volver a Inventario
            </Button>
          </Alert>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
      <Row className="justify-content-center">
        <Col lg={8}>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">
                <i className={`bi ${isEditMode ? 'bi-pencil-square' : 'bi-plus-circle'} me-2`}></i>
                {isEditMode
                  ? (watchItemType === 'tool' ? 'Editar Herramienta' : 'Editar Material')
                  : (watchItemType === 'tool' ? 'Nueva Herramienta' : 'Nuevo Material')}
              </h4>
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  {/* Name */}
                  <Col md={8}>
                    <Form.Group className="mb-3" controlId="name">
                      <Form.Label>
                        Nombre <span className="text-danger">*</span>
                      </Form.Label>
                      <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="text"
                            placeholder="Ej: Tornillo de acero inoxidable"
                            isInvalid={!!errors.name}
                            disabled={isSubmitting}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  {/* SKU */}
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="sku">
                      <Form.Label>SKU</Form.Label>
                      <Controller
                        name="sku"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="text"
                            placeholder="Auto-generado"
                            isInvalid={!!errors.sku}
                            disabled={isSubmitting || (autoGenerateSKU && !isEditMode)}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.sku?.message}
                      </Form.Control.Feedback>
                      {!isEditMode && (
                        <Form.Check
                          type="checkbox"
                          label="Auto-generar"
                          checked={autoGenerateSKU}
                          onChange={(e) => setAutoGenerateSKU(e.target.checked)}
                          className="mt-1"
                        />
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {/* Description */}
                <Form.Group className="mb-3" controlId="description">
                  <Form.Label>Descripción</Form.Label>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        as="textarea"
                        rows={2}
                        placeholder="Descripción del material..."
                        isInvalid={!!errors.description}
                        disabled={isSubmitting}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.description?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Row>
                  {/* Category */}
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="category">
                      <Form.Label>Categoría</Form.Label>
                      <Controller
                        name="category"
                        control={control}
                        render={({ field }) => (
                          <Form.Select
                            {...field}
                            isInvalid={!!errors.category}
                            disabled={isSubmitting}
                          >
                            <option value="">Seleccionar categoria...</option>
                            {Object.entries(INVENTORY_CATEGORY_LABELS).map(([key, label]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </Form.Select>
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.category?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  {/* Item Type */}
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="item_type">
                      <Form.Label>Tipo de ítem</Form.Label>
                      <Controller
                        name="item_type"
                        control={control}
                        render={({ field }) => (
                          <Form.Select {...field} disabled={isSubmitting}>
                            <option value="consumable">Consumible (FIFO)</option>
                            <option value="tool">Herramienta (préstamo serializado)</option>
                          </Form.Select>
                        )}
                      />
                      <Form.Text className="text-muted">
                        Herramienta: requiere registrar assets por serial antes de asignar
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  {/* Ubicación por defecto (decisión 8 — Jorge) */}
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="default_location_id">
                      <Form.Label>Bodega</Form.Label>
                      <Controller
                        name="default_location_id"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <LocationPicker
                            id="default_location_id"
                            value={value || ''}
                            onChange={onChange}
                            disabled={isSubmitting}
                            placeholder="Bodega Principal (por defecto)"
                            showCount={false}
                            searchable
                          />
                        )}
                      />
                      <Form.Text className="text-muted">
                        Si no elegís otra, el material queda en la bodega principal.
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  {/* Track Batches Toggle */}
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="track_batches">
                      <Form.Label>Control de Lotes</Form.Label>
                      <Controller
                        name="track_batches"
                        control={control}
                        render={({ field: { value, onChange, ...field } }) => (
                          <Form.Check
                            {...field}
                            type="switch"
                            label={value ? 'Rastrear lotes (FIFO)' : 'Sin control de lotes'}
                            checked={value}
                            onChange={(e) => onChange(e.target.checked)}
                            disabled={isSubmitting}
                            className="mt-2"
                          />
                        )}
                      />
                      <Form.Text className="text-muted">
                        Habilitar para rastrear lotes con costos individuales y usar FIFO
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  {/* Stockable Toggle (#103) */}
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="is_stockable">
                      <Form.Label>¿Va a bodega?</Form.Label>
                      <Controller
                        name="is_stockable"
                        control={control}
                        render={({ field: { value, onChange, ...field } }) => (
                          <Form.Check
                            {...field}
                            type="switch"
                            label={value ? 'Sí, pasa por inventario' : 'No, se compra directo al proyecto'}
                            checked={value}
                            onChange={(e) => onChange(e.target.checked)}
                            disabled={isSubmitting}
                            className="mt-2"
                          />
                        )}
                      />
                      <Form.Text className="text-muted">
                        Marcalo si el item va a bodega y se consume FIFO. Desmarcalo si es de compra directa al proyecto, sin pasar por stock — cada compra se carga como gasto al proyecto.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Image Upload — #798: no se le ofrece a quien el bucket rechaza */}
                {!puedeSubirImagen && (
                  <div className="alert alert-light border py-2 mb-4 small">
                    <i className="bi bi-info-circle me-1"></i>
                    La imagen la agrega un administrador después de crear el ítem.
                  </div>
                )}
                {puedeSubirImagen && (
                <Form.Group className="mb-4" controlId="image">
                  <Form.Label>
                    Imagen {watchItemType === 'tool' ? 'de la Herramienta' : 'del Producto'}{' '}
                    {watchItemType === 'tool'
                      ? <span className="text-warning fw-semibold">(Recomendada)</span>
                      : <span className="text-muted">(Opcional)</span>}
                  </Form.Label>
                  {watchItemType === 'tool' && (
                    <div className="alert alert-warning py-2 mb-2 small">
                      <i className="bi bi-lightbulb me-1"></i>
                      Agrega una foto para facilitar la identificación visual en el catálogo y al asignar.
                    </div>
                  )}
                  <div className="d-flex flex-column flex-sm-row gap-3 align-items-start">
                    {/* Image Preview */}
                    {imagePreview ? (
                      <div className="position-relative" style={{ width: '150px', height: '150px' }}>
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="rounded border"
                          style={{
                            width: '150px',
                            height: '150px',
                            objectFit: 'cover',
                          }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0 m-1"
                          onClick={handleRemoveImage}
                          disabled={removingImage || isSubmitting}
                          title="Eliminar imagen"
                        >
                          {removingImage ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <i className="bi bi-x-lg"></i>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center rounded border bg-light"
                        style={{ width: '150px', height: '150px' }}
                      >
                        <i className="bi bi-image text-muted fs-1"></i>
                      </div>
                    )}

                    {/* Upload Input */}
                    <div className="flex-grow-1">
                      <Form.Control
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleImageSelect}
                        disabled={isSubmitting || uploadingImage}
                      />
                      <Form.Text className="text-muted">
                        Formatos: JPG, PNG, WebP. Máximo 5MB.
                      </Form.Text>
                      {uploadingImage && (
                        <div className="mt-2">
                          <Spinner animation="border" size="sm" className="me-2" />
                          <small>Subiendo imagen...</small>
                        </div>
                      )}
                    </div>
                  </div>
                </Form.Group>
                )}

                <Row>
                  {/* Unit */}
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="unit">
                      <Form.Label>
                        Unidad
                      </Form.Label>
                      <Controller
                        name="unit"
                        control={control}
                        render={({ field }) => (
                          <Form.Select
                            {...field}
                            isInvalid={!!errors.unit}
                            disabled={isSubmitting}
                          >
                            {UNITS.map((unit) => (
                              <option key={unit.value} value={unit.value}>
                                {unit.label}
                              </option>
                            ))}
                          </Form.Select>
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.unit?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  {/* Sale Price (Precio de Lista) */}
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="sale_price">
                      <Form.Label>
                        Precio de Lista
                      </Form.Label>
                      <Controller
                        name="sale_price"
                        control={control}
                        render={({ field }) => (
                          <InputGroup>
                            <InputGroup.Text>CRC</InputGroup.Text>
                            <Form.Control
                              {...field}
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              isInvalid={!!errors.sale_price}
                              disabled={isSubmitting}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.sale_price?.message}
                            </Form.Control.Feedback>
                          </InputGroup>
                        )}
                      />
                      <Form.Text className="text-muted">
                        Precio de venta al cliente
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  {/* Unit Cost (Deprecado - solo lectura si hay lotes) */}
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="unit_cost">
                      <Form.Label>
                        Costo Base <small className="text-muted">(referencia)</small>
                      </Form.Label>
                      <Controller
                        name="unit_cost"
                        control={control}
                        render={({ field }) => (
                          <InputGroup>
                            <InputGroup.Text>CRC</InputGroup.Text>
                            <Form.Control
                              {...field}
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              isInvalid={!!errors.unit_cost}
                              disabled={isSubmitting}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.unit_cost?.message}
                            </Form.Control.Feedback>
                          </InputGroup>
                        )}
                      />
                      <Form.Text className="text-muted">
                        El costo real se calcula desde los lotes
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  {/* Current Stock */}
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="current_stock">
                      <Form.Label>
                        Stock Actual
                      </Form.Label>
                      <Controller
                        name="current_stock"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="number"
                            step="1"
                            placeholder="0"
                            isInvalid={!!errors.current_stock}
                            disabled={isSubmitting}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.current_stock?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  {/* Min Stock Level */}
                  <Col md={6}>
                    <Form.Group className="mb-4" controlId="min_stock_level">
                      <Form.Label>
                        Stock Mínimo
                      </Form.Label>
                      <Controller
                        name="min_stock_level"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="number"
                            step="1"
                            placeholder="0"
                            isInvalid={!!errors.min_stock_level}
                            disabled={isSubmitting}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.min_stock_level?.message}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Se generará una alerta cuando el stock esté por debajo de este nivel
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Action Buttons */}
                <div className="d-flex justify-content-end gap-2">
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting || loading}
                  >
                    {isSubmitting || loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {isEditMode ? 'Actualizando...' : 'Creando...'}
                      </>
                    ) : (
                      <>
                        <i className={`bi ${isEditMode ? 'bi-check-lg' : 'bi-plus-lg'} me-2`}></i>
                        {isEditMode ? 'Actualizar Material' : 'Crear Material'}
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      </Container>
    </DashboardLayout>
  );
};

export default MaterialForm;
